import { AuthForm, InputModal, StatusTag } from "@packages/ui/components";
import { CircularProgress, VeBox, VeTypography } from "@packages/ui/mui";
import {
  ORG_ICON,
  RIGHT_ARROW_ICON,
  VE_ICON_PURPLE,
  INVESTOR_ICON,
  VERIFIER,
  SALES_PARTNER,
} from "@public/index";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { mutateAction } from "@libs/actions/mutateAction";
import { toast } from "@libs/helper/toast";
import Image from "next/image";
import React from "react";
import { useSession } from "next-auth/react";
import { useData } from "@providers/context";

interface SelectRoleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const SelectRoleModal: React.FC<SelectRoleModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const roles: Record<string, string> = {
    SELLER: "Project Developer",
    // INVESTOR: "Investor",
    PROJECT_REVIEWER: "Verifier",
    SALES_PARTNER: "Sales Partner",
  };

  const roleIcons: Record<string, any> = {
    SELLER: ORG_ICON,
    // INVESTOR: INVESTOR_ICON,
    PROJECT_REVIEWER: VERIFIER,
    SALES_PARTNER: SALES_PARTNER,
  };

  const { data: session, update } = useSession();
  const queryClient = useQueryClient();
  const { userData: user, setCurrentRole } = useData();
  const [selectedRole, setSelectedRole] = React.useState<string | null>(null);

  // Parse user's internal roles
  const getUserRoles = (): string[] => {
    try {
      if (user?.user?.internalRoleName) {
        // Check if it's already an array
        if (Array.isArray(user.user.internalRoleName)) {
          return user.user.internalRoleName;
        }
        // If it's a string, try to parse it
        if (typeof user.user.internalRoleName === "string") {
          const parsed = JSON.parse(user.user.internalRoleName);
          // Handle {"json":["ROLE1","ROLE2"]} format
          if (parsed?.json && Array.isArray(parsed.json)) {
            return parsed.json;
          }
          // Handle direct array string format
          if (Array.isArray(parsed)) {
            return parsed;
          }
        }
      }
    } catch (error) {
      console.error("Error parsing user roles:", error);
    }
    return [];
  };

  const userRoles = getUserRoles();

  // Filter roles to only show those that user has access to
  const availableRoles = Object.entries(roles).filter(([roleKey]) =>
    userRoles.includes(roleKey)
  );

  const { mutate: updateRoleSelection, isPending } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["update-role-selection"],
    onSuccess: async (data) => {
      if (data.error)
        throw new Error(data.error?.heading, { cause: data?.error?.message });

      // Refetch user profile after role selection
      await queryClient.refetchQueries({ queryKey: ["userProfile"] });
      await queryClient.refetchQueries({ queryKey: ["permissionData"] });
      setCurrentRole(selectedRole);

      // Notify other tabs about role change so they can reload
      try {
        // BroadcastChannel for modern browsers
        if (typeof window !== "undefined" && (window as any).BroadcastChannel) {
          try {
            const bc = new (window as any).BroadcastChannel(
              "vericap_role_change"
            );
            bc.postMessage({ time: Date.now() });
            bc.close();
          } catch (err) {
            // ignore
          }
        }
        // localStorage fallback to trigger storage event in other tabs
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem(
            "vericap_role_change",
            Date.now().toString()
          );
        }
      } catch (err) {
        console.error("Error broadcasting role change:", err);
      }

      // Close modal and call onSuccess callback
      onClose();
      onSuccess?.();

      // Navigate current tab to dashboard to ensure permissions and UI reflect the new role
      try {
        if (typeof window !== "undefined") window.location.assign("/dashboard");
      } catch (err) {
        console.error(
          "Error navigating current tab to /dashboard after role change:",
          err
        );
      }
    },
    onError: (error) => {
      toast(error?.cause as string, { type: "error" });
      setSelectedRole(null);
    },
  });

  const handleRoleSelection = (roleKey: string) => {
    // Set the selected role to show loader immediately
    setSelectedRole(roleKey);

    // Update the session with the selected role
    update({
      ...session,
      user: {
        ...session?.user,
        selectedRole: roleKey,
      },
    });

    // Call the API to update the role
    updateRoleSelection({
      type: "patch",
      endPoint: "/users/profile",
      data: {
        lastLoggedInRole: roleKey,
      },
      session,
      update,
    });
  };

  return (
    <InputModal open={open} onClose={onClose} paddingBottomReduce={0}>
      <AuthForm
        border={false}
        heading={`Select Role`}
        marginBottom="8px"
        style={{ padding: "0px" }}
      >
        <VeBox
          width="100%"
          display={"flex"}
          flexDirection={"column"}
          gap="32px"
        >
          <VeTypography variant="body1Secondary" textAlign={"center"}>
            Select the role you would like to work on.
          </VeTypography>
          <VeBox display={"flex"} flexDirection={"column"} gap="16px">
            {availableRoles.length > 0 ? (
              availableRoles.map(([roleKey, roleName]) => {
                const isThisRolePending = isPending && selectedRole === roleKey;
                const currentRole = user?.user?.lastLoggedInRole === roleKey;
                return (
                  <VeBox
                    key={roleKey}
                    border={
                      currentRole ? "1px solid #703DDE" : "1px solid #D5DBE1"
                    }
                    borderRadius={"4px"}
                    padding={"12px"}
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    onClick={() =>
                      !isPending && !currentRole && handleRoleSelection(roleKey)
                    }
                    sx={{
                      cursor: isPending ? "not-allowed" : "pointer",
                      opacity: isThisRolePending ? 0.6 : 1,

                      "&:hover": {
                        border: isPending
                          ? "1px solid #D5DBE1"
                          : "1px solid #703DDE",
                      },
                    }}
                  >
                    <VeBox
                      display={"flex"}
                      alignItems={"center"}
                      gap="12px"
                      flex={1}
                    >
                      <VeBox
                        padding={"11px"}
                        borderRadius={"100px"}
                        bgcolor={"#F5F4FA"}
                        display={"flex"}
                      >
                        <Image src={roleIcons[roleKey]} alt="ROLE_ICON" />
                      </VeBox>
                      <VeTypography variant="label2StrongMain">
                        {roleName}
                      </VeTypography>
                    </VeBox>
                    {currentRole ? (
                      <StatusTag status="primaryUser" noBorder color="#703DDE">
                        Current
                      </StatusTag>
                    ) : isThisRolePending ? (
                      <CircularProgress size={20} sx={{ color: "#703DDE" }} />
                    ) : (
                      <Image src={RIGHT_ARROW_ICON} alt="RIGHT_ARROW_ICON" />
                    )}
                  </VeBox>
                );
              })
            ) : (
              <VeBox padding="20px" textAlign="center">
                <VeTypography variant="body2Secondary">
                  No roles available for your account. Please contact support.
                </VeTypography>
              </VeBox>
            )}
          </VeBox>
        </VeBox>
      </AuthForm>
    </InputModal>
  );
};

export default SelectRoleModal;
